import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Search as SearchIcon, X, SlidersHorizontal } from 'lucide-react';
import { COLLECTIONS, PRODUCTS } from '../mock';

const Collection = () => {
  const { collectionId } = useParams();
  const col = COLLECTIONS.find((c) => c.id === collectionId);
  const items = useMemo(() => PRODUCTS.filter((p) => p.collectionId === collectionId), [collectionId]);

  const maxPrice = useMemo(() => items.reduce((m, p) => Math.max(m, p.price), 0) || 1000, [items]);
  const [query, setQuery] = useState('');
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(maxPrice);
  const [activeBrand, setActiveBrand] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All');
  const [sort, setSort] = useState('featured');
  const [filtersOpen, setFiltersOpen] = useState(false);

  React.useEffect(() => { setPriceMax(maxPrice); }, [maxPrice]);

  const brands = useMemo(() => ['All', ...Array.from(new Set(items.map((p) => p.brand)))], [items]);
  const categories = useMemo(() => ['All', ...Array.from(new Set(items.map((p) => p.category)))], [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items.filter((p) => {
      if (q && !`${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(q)) return false;
      if (p.price < priceMin || p.price > priceMax) return false;
      if (activeBrand !== 'All' && p.brand !== activeBrand) return false;
      if (activeCategory !== 'All' && p.category !== activeCategory) return false;
      return true;
    });
    if (sort === 'price-low') list = [...list].sort((a, b) => a.price - b.price);
    if (sort === 'price-high') list = [...list].sort((a, b) => b.price - a.price);
    if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [items, query, priceMin, priceMax, activeBrand, activeCategory, sort]);

  const reset = () => {
    setQuery('');
    setPriceMin(0);
    setPriceMax(maxPrice);
    setActiveBrand('All');
    setActiveCategory('All');
    setSort('featured');
  };

  const activeFilterCount =
    (query ? 1 : 0) +
    (activeBrand !== 'All' ? 1 : 0) +
    (activeCategory !== 'All' ? 1 : 0) +
    (priceMin > 0 || priceMax < maxPrice ? 1 : 0);

  React.useEffect(() => {
    if (filtersOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [filtersOpen]);

  const filterControls = (
    <>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display tracking-[0.22em] text-base font-bold">FILTERS</h2>
        <button onClick={reset} className="text-xs text-[#FF6B00] hover:underline font-display tracking-[0.18em]" data-testid="collection-reset">RESET</button>
      </div>

      <div className="mb-6">
        <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-2">SEARCH</div>
        <div className="relative">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search in collection..."
            className="w-full bg-white border border-neutral-300 pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:border-[#FF6B00]"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">PRICE RANGE</div>
        <div className="flex items-center gap-2 w-full">
          <input
            type="number"
            value={priceMin}
            min={0}
            max={priceMax}
            onChange={(e) => setPriceMin(Math.max(0, Math.min(priceMax, Number(e.target.value) || 0)))}
            className="flex-1 min-w-0 bg-white border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B00]"
          />
          <span className="text-neutral-400 flex-shrink-0">&ndash;</span>
          <input
            type="number"
            value={priceMax}
            min={priceMin}
            max={maxPrice}
            onChange={(e) => setPriceMax(Math.min(maxPrice, Math.max(priceMin, Number(e.target.value) || 0)))}
            className="flex-1 min-w-0 bg-white border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B00]"
          />
        </div>
        <div className="relative h-1 bg-neutral-200 mt-4">
          <div
            className="absolute h-1 bg-[#FF6B00]"
            style={{
              left: `${(priceMin / maxPrice) * 100}%`,
              right: `${100 - (priceMax / maxPrice) * 100}%`,
            }}
          />
        </div>
        <div className="flex justify-between text-[11px] text-neutral-400 mt-2">
          <span>${priceMin.toLocaleString()}</span>
          <span>${priceMax.toLocaleString()}</span>
        </div>
      </div>

      {brands.length > 2 && (
        <div className="mb-6">
          <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">BRAND</div>
          <div className="flex flex-wrap gap-2">
            {brands.map((b) => (
              <button
                key={b}
                onClick={() => setActiveBrand(b)}
                className={`text-[11px] font-display tracking-[0.18em] px-3 py-1.5 border ${
                  activeBrand === b ? 'bg-[#FF6B00] border-[#FF6B00] text-white' : 'border-neutral-300 hover:border-neutral-900'
                }`}
              >
                {b.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      {categories.length > 2 && (
        <div>
          <div className="font-display tracking-[0.2em] text-[11px] text-neutral-500 mb-3">CATEGORY</div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`text-[11px] font-display tracking-[0.18em] px-3 py-1.5 border ${
                  activeCategory === c ? 'bg-[#FF6B00] border-[#FF6B00] text-white' : 'border-neutral-300 hover:border-neutral-900'
                }`}
              >
                {c.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );

  if (!col) {
    return (
      <div className="bg-white min-h-screen pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-neutral-900">
          <h1 className="font-display text-4xl">Collection not found</h1>
          <Link to="/" className="text-[#FF6B00] underline mt-6 inline-block">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white text-neutral-900 overflow-x-hidden">
      <section
        className="relative pt-40 pb-20"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.85), rgba(0,0,0,0.55)), url(${col.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-white">
          <div className="font-display tracking-[0.3em] text-sm text-[#FF6B00] font-semibold mb-3">{col.years}</div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight break-words">{col.name.toUpperCase()}</h1>
          <p className="text-neutral-200 text-lg mt-5 max-w-2xl leading-relaxed">{col.tagline}</p>
          {col.descriptionHtml && (
            <div
              className="prose-html max-w-2xl mt-5 leading-relaxed text-neutral-200 [&_p]:text-neutral-200 [&_li]:text-neutral-200 [&_strong]:text-white [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_a]:text-[#FF6B00] [&_a]:underline"
              data-testid="collection-description"
              dangerouslySetInnerHTML={{ __html: col.descriptionHtml.replace(/&nbsp;|\u00a0/g, ' ') }}
            />
          )}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-[15px] mb-8 text-neutral-500">
            <Link to="/" className="hover:text-[#FF6B00]">Home</Link>
            <span>/</span>
            <span className="text-neutral-900">{col.name}</span>
          </div>

          {items.length === 0 ? (
            <div className="text-neutral-500 py-20 text-center">No products in this collection yet. Reach out for custom builds.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10">
              {/* Mobile filter trigger */}
              <div className="lg:hidden flex items-center justify-between -mb-2">
                <div className="text-sm text-neutral-600">
                  <span className="font-display tracking-[0.18em] text-neutral-900">{filtered.length}</span> of {items.length} item{items.length !== 1 ? 's' : ''}
                </div>
                <button
                  onClick={() => setFiltersOpen(true)}
                  data-testid="collection-filters-toggle"
                  className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2.5 text-xs font-display tracking-[0.18em] text-neutral-900 hover:border-neutral-900"
                >
                  <SlidersHorizontal size={15} /> FILTERS{activeFilterCount ? ` (${activeFilterCount})` : ''}
                </button>
              </div>

              {/* Filters — desktop sidebar */}
              <aside className="hidden lg:block lg:sticky lg:top-28 lg:self-start">
                {filterControls}
              </aside>

              {/* Grid */}
              <div>
                <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                  <div className="hidden lg:block text-base text-neutral-600">
                    <span className="font-display tracking-[0.18em] text-neutral-900">{filtered.length}</span> of {items.length} item{items.length !== 1 ? 's' : ''}
                  </div>
                  <div className="flex items-center gap-2 text-sm ml-auto">
                    <span className="text-neutral-500 font-display tracking-[0.2em] text-xs">SORT</span>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-neutral-300 px-3 py-2 text-base focus:outline-none focus:border-[#FF6B00]">
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name (A&ndash;Z)</option>
                    </select>
                  </div>
                </div>

                {filtered.length === 0 ? (
                  <div className="py-16 text-center text-neutral-500">
                    <p className="mb-3">No products match your filters.</p>
                    <button onClick={reset} className="text-[#FF6B00] hover:underline font-display tracking-[0.18em] text-sm">RESET FILTERS</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-10">
                    {filtered.map((p) => {
                      const onSale = p.compareAtPrice && p.compareAtPrice > p.price;
                      const hoverImg = p.images[1] || p.images[0];
                      return (
                        <Link key={p.id} to={`/product/${p.slug}`} className="group block motion-fad" data-testid={`collection-product-${p.id}`}>
                          <div className="relative aspect-square bg-neutral-100 overflow-hidden mb-4">
                            <img
                              src={p.images[0]}
                              alt={p.name}
                              loading="lazy"
                              className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                            />
                            {hoverImg !== p.images[0] && (
                              <img
                                src={hoverImg}
                                alt=""
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                              />
                            )}
                            <span className="absolute left-0 right-0 bottom-0 h-[2px] bg-[#FF6B00] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                            <span className="absolute top-3 left-3 bg-white/95 text-neutral-900 text-[10px] font-display tracking-[0.22em] px-2 py-1 backdrop-blur">
                              MADE TO ORDER
                            </span>
                            {onSale && (
                              <span className="absolute top-3 right-3 bg-[#FF6B00] text-white text-[10px] font-display tracking-[0.22em] px-2 py-1">
                                SALE
                              </span>
                            )}
                          </div>
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <div className="font-display tracking-[0.22em] text-[11px] text-[#FF6B00] font-semibold mb-1.5">{p.brand.toUpperCase()}</div>
                              <div className="flex items-baseline gap-2 flex-wrap">
                                <span className="font-display text-2xl font-bold tracking-tight text-neutral-900 leading-none">${p.price.toLocaleString()}</span>
                                <span className="text-neutral-400 text-[11px]">USD</span>
                                {onSale && (
                                  <span className="text-neutral-400 line-through text-sm">${p.compareAtPrice.toLocaleString()}</span>
                                )}
                              </div>
                              <h3 className="font-display text-base font-normal tracking-wide text-neutral-500 leading-snug mt-1.5 group-hover:text-[#FF6B00] transition-colors truncate">{p.name}</h3>
                            </div>
                            <ArrowRight size={18} className="text-neutral-400 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-all mt-1 flex-shrink-0" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Mobile filters drawer */}
      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" data-testid="collection-filters-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setFiltersOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
              <span className="font-display tracking-[0.22em] text-sm font-bold text-neutral-900">FILTERS</span>
              <button onClick={() => setFiltersOpen(false)} className="h-9 w-9 flex items-center justify-center text-neutral-500 hover:text-neutral-900" data-testid="collection-filters-close">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              {filterControls}
            </div>
            <div className="border-t border-neutral-200 p-4">
              <button
                onClick={() => setFiltersOpen(false)}
                className="w-full bg-black hover:bg-[#FF6B00] text-white py-3.5 font-display tracking-[0.2em] text-sm transition-colors"
                data-testid="collection-filters-apply"
              >
                VIEW {filtered.length} RESULT{filtered.length !== 1 ? 'S' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collection;
